/**
 * Where a subthread came from (RFC 0302 S4).
 *
 * A branch is a child session whose log opens with `mod.session_branched`. This
 * projects that one entry back out: the session it branched from, the post that
 * caused it, and the frontier it was taken against.
 *
 * ONE, not a list. A subthread does not nest again (3d), so a session is a
 * branch of at most one other, and the first such entry wins — a second could
 * only be a duplicate write, and picking the earliest makes a re-run idempotent
 * rather than changing where Back goes.
 */

import { topoOrder } from './projection-order'
import type { LogDoc } from './types'
import { SESSION_BRANCHED_EXTENSION } from './types'

export interface SessionBranch {
  /** The session this one branched from. */
  sourceSessionId: string
  /** The post that caused it, on that session's log. Empty when unknown. */
  sourceMessageId: string
  /** The causal frontier the branch was taken against. */
  sourceHeads: string[]
  at: number
}

export function projectSessionBranch(doc: LogDoc): SessionBranch | null {
  for (const e of topoOrder(doc)) {
    const p = e.payload
    if (p.type !== 'extension') continue
    const { extensionType, payload } = p.payload
    if (extensionType !== SESSION_BRANCHED_EXTENSION) continue
    // Opaque payload, so coerce: an extension's inner payload is left raw by
    // the codec and every element of a list arrives as an Automerge proxy.
    const rawHeads = payload.sourceHeads
    return {
      sourceSessionId:
        payload.sourceSessionId != null ? String(payload.sourceSessionId) : '',
      sourceMessageId:
        payload.sourceMessageId != null ? String(payload.sourceMessageId) : '',
      sourceHeads: Array.isArray(rawHeads)
        ? rawHeads.map((h) => String(h))
        : [],
      at: payload.at != null ? Number(payload.at) : 0,
    }
  }
  return null
}
